"use client";

import { useState, useRef, useEffect } from "react";
import { ArrowLeft, ArrowUp } from "lucide-react";
import { agriChat } from "../api";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';


const AGRI_SUGGESTIONS = [
"Which crops grow best in the rainy season?",
"When should I plant maize and cassava?",
"How can I improve soil fertility sustainably?",
"How do I prevent and treat common pests and diseases?",
];



export function AgriChatbot() {
  const router = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: "1",
      text:"Hi! I'm your Agriculture assistant. Ask me about crops, planting, soil health, pest management or farming best practices. How can I help your farm today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, );

  const handleSendMessage = async (text) => {
    const messageText = text || inputValue.trim();
    if (!messageText) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      text: messageText,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await agriChat(messageText);

      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: response.answer || "I couldn't get a response. Try again!",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const botMessage = {
        id: (Date.now() + 1).toString(),
        text:
          error instanceof Error
            ? `Error: ${error.message}`
            : "Something went wrong. Please try again.",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white">

      <div className="bg-white/80 backdrop-blur-sm border-b-2 border-green-800 px-4 md:px-6 py-4 md:py-5 sticky top-0">
        <div className="relative flex items-center justify-center w-full">
          <button
            onClick={() => router("/")}
            size="icon"
            className="absolute left-0 cursor-pointer text-greeen-800"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-green-600 font-bold">EYES Ghana</h2>
          </div>
        </div>
      </div>


      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs md:max-w-md lg:max-w-xl px-4 py-3 rounded-2xl ${
                message.sender === "user"
                  ? "bg-green-800 text-white rounded-br-none"
                  : "bg-white text-black border border-green-800 rounded-bl-none shadow-sm"
              }`}
            >
              {message.sender === "user" ? (
        <p className="text-sm md:text-base leading-relaxed">
          {message.text}
        </p>
      ) : (
        <div 
          className="text-sm md:text-base leading-relaxed prose prose-sm max-w-none prose-p:leading-relaxed prose-p:my-0 prose-headings:my-2 prose-ul:my-0 prose-li:my-0"
        >
          <ReactMarkdown>
            {message.text}
          </ReactMarkdown>

        </div>
      )}
              <span
                className={`text-xs mt-1 block ${
                  message.sender === "user"
                    ? "text-white/70"
                    : "text-muted-foreground"
                }`}
              >
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white text-black px-4 py-3 rounded-2xl rounded-bl-none border border-pink-100 shadow-sm">
              <div className="flex gap-2">
                <div
                  className="w-2 h-2 rounded-full bg-green-800 animate-bounce"
                  style={{ animationDelay: "0s" }}
                />
                <div
                  className="w-2 h-2 rounded-full bg-green-800 animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
                <div
                  className="w-2 h-2 rounded-full bg-green-800 animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="px-4 md:px-6 pb-4">
          <p className="text-xs md:text-sm text-black mb-3 font-semibold">
            Try asking:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {AGRI_SUGGESTIONS.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSendMessage(suggestion)}
                className="text-left px-3 py-2 rounded-lg bg-white border border-green-800 hover:bg-green-300 transition-colors text-sm text-black"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white px-4 md:px-6 py-4 md:py-5 border-t border-green-800">
        <div className="flex gap-2 md:gap-3 max-w-4xl mx-auto">
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && !isLoading && handleSendMessage()
              }
              placeholder="Ask me anything about Viral Beauty & the beauty industry"
              className="w-full px-4 py-3 rounded-lg bg-white/50 text-black border border-green-800 focus:outline-none focus:border-green-700 focus:ring-1 focus:ring-green-700 placeholder-gray-400 text-sm md:text-base transition-all pr-12"
              disabled={isLoading}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded-full
               w-8 h-8 bg-green-800 hover:bg-green-700 border-white border-2
               text-white flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
